import { useAuth } from "@Auth/useAuth";
import { ConfirmationModal } from "@common/ConfirmationModal/ConfirmationModal";
import styled from "@emotion/styled";
import { Check, WarningAmberRounded } from "@mui/icons-material";
import { Theme, useTheme } from "@mui/material";
import { ErrorBoundary } from "./ErrorBoundary";
import { useOrganizationStore } from "@stores/organizationStore";
import { useTagsStore } from "@stores/tagsStore";
import {
  MaterialDesignContent,
  SnackbarProvider,
  SnackbarProviderProps,
} from "notistack";
import { useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";

// import '@analytics/init';

// Styled components
const Main = styled.main`
  height: 100vh;
  overflow: hidden;
  width: 100%;
`;

// Styles
const createSnackbarStyles = (theme: Theme) =>
  styled(MaterialDesignContent)(() => ({
    "&.notistack-MuiContent": {
      height: 47,
      minWidth: 346,
      padding: "12px 20px",
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "24px",
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      fontFamily: "Roboto, sans-serif",

      "> div#notistack-snackbar": {
        padding: 0,
        gap: 8,
      },
    },

    "&.notistack-MuiContent-info": {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    "&.notistack-MuiContent-error": {
      gap: 20,
      backgroundColor: theme.palette.mode === "dark" ? "#1d1412" : "#fffcfc",
      border: `1px solid ${
        theme.palette.mode === "dark" ? "#7f2315" : "#f3b0a2"
      }`,
    },
    "&.notistack-MuiContent-warning": {
      backgroundColor: theme.palette.warning.light,
      border: `1px solid ${theme.palette.warning.dark}`,
    },
    "&.notistack-MuiContent-success": {
      backgroundColor: theme.palette.success.light,
      border: `1px solid ${theme.palette.success.dark}`,
    },
  }));

const Root = () => {
  const theme = useTheme();
  const { baseUser } = useAuth();
  const { getOrganization } = useOrganizationStore();
  const { getTagValuesByOrganizationId } = useTagsStore();

  useEffect(() => {
    if (!baseUser?.organization_id) return;
    getOrganization(baseUser.organization_id, baseUser.id);
    getTagValuesByOrganizationId(baseUser.organization_id);
  }, [baseUser, getOrganization, getTagValuesByOrganizationId]);

  // Memoize snackbar styles
  const snackbarStyles = useMemo(() => createSnackbarStyles(theme), [theme]);

  // Memoize snackbar icons
  const snackbarIcons = useMemo(
    () => ({
      success: <Check sx={{ color: theme.palette.success.main }} />,
      error: <WarningAmberRounded color="error" />,
      warning: <WarningAmberRounded color="warning" />,
    }),
    [theme]
  );

  // Memoize snackbar components configuration
  const snackbarComponents: SnackbarProviderProps["Components"] = useMemo(
    () => ({
      success: snackbarStyles,
      error: snackbarStyles,
      info: snackbarStyles,
      warning: snackbarStyles,
    }),
    [snackbarStyles]
  );

  return (
    <SnackbarProvider
      autoHideDuration={5000}
      Components={snackbarComponents}
      iconVariant={snackbarIcons}
      maxSnack={3}
    >
      <ErrorBoundary>
        <Main>
          <ConfirmationModal />
          <Outlet />
        </Main>
      </ErrorBoundary>
    </SnackbarProvider>
  );
};

export default Root;
